import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";

export interface RegionsParams {
  dates?: string[];
  sources?: string[];
  excludedSources?: string[];
  categories?: string[];
  excludedCategories?: string[];
  timePeriods?: string[];
}

export interface RegionWithCount {
  region: string;
  count: number;
}

export interface RegionsResponse {
  regions: RegionWithCount[];
}

function buildQueryKey(params: RegionsParams): string[] {
  const base = '/api/regions';
  const searchParams = new URLSearchParams();
  
  if (params.dates && params.dates.length > 0) {
    searchParams.set('dates', params.dates.join(','));
  }
  
  if (params.sources && params.sources.length > 0) {
    params.sources.forEach(s => searchParams.append('sources', s));
  }
  
  if (params.excludedSources && params.excludedSources.length > 0) {
    params.excludedSources.forEach(s => searchParams.append('excludedSources', s));
  }
  
  if (params.categories && params.categories.length > 0) {
    params.categories.forEach(cat => searchParams.append('categories', cat));
  }
  
  if (params.excludedCategories && params.excludedCategories.length > 0) {
    params.excludedCategories.forEach(cat => searchParams.append('excludedCategories', cat));
  }
  
  if (params.timePeriods && params.timePeriods.length > 0) {
    searchParams.set('timePeriods', params.timePeriods.join(','));
  }
  
  const queryString = searchParams.toString();
  return queryString ? [`${base}?${queryString}`] : [base];
}

export function useRegions(params: RegionsParams = {}) {
  const queryKey = buildQueryKey(params);
  
  return useQuery<RegionsResponse>({
    queryKey,
    queryFn: getQueryFn<RegionsResponse>({ on401: 'throw' }),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}
